import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../context/CurrencyContext.tsx";
import { useTheme } from "../context/ThemeContext.tsx";
import { percentage } from "../shared/percentage";
import { getColorForCosts } from "./analytics/CostColors.ts";
import { TABULAR_NUMERALS } from "./general/dataAtoms/shared.ts";

/**
 * How much of a party's costs is spent: the share as a figure, a bar of what
 * is incurred against what is still to come, and the two amounts beside the
 * colours they have in the bar.
 */
export function ProgressBar({
  incurred,
  total,
}: {
  incurred: number;
  total: number;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "CostProgressBar" });
  const { currency } = useCurrency();
  const percent = total ? incurred / total : 0;

  const colorIncurred = getColorForCosts("incurred", theme).backgroundColor;
  const colorEstimated = getColorForCosts("estimated", theme).backgroundColor;

  const amount = (color: string, label: string) => (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: 0.5,
          bgcolor: color,
          flex: "none",
        }}
      />
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
      >
        {label}
      </Typography>
    </Stack>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Typography sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}>
        {t("progress", {
          percent: percentage.format(percent),
          total: currency.format(total),
        })}
      </Typography>

      <Box
        sx={{
          height: theme.spacing(1),
          borderRadius: 999,
          bgcolor: colorEstimated,
          overflow: "hidden",
          width: "100%",
          mt: 1,
        }}
      >
        <Box
          sx={{
            width: `${percent * 100}%`,
            height: "100%",
            bgcolor: colorIncurred,
            transition: "width .3s ease",
          }}
        />
      </Box>
      <Stack
        direction="row"
        useFlexGap
        sx={{
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: theme.spacing(0.5, 2),
          mt: 0.75,
        }}
      >
        {amount(
          colorIncurred,
          t("chip.incurred", { value: currency.format(incurred) }),
        )}
        {amount(
          colorEstimated,
          t("chip.estimated", { value: currency.format(total - incurred) }),
        )}
      </Stack>
    </Box>
  );
}
